import React, { useState } from "react";
import { analysisDescription } from "../Utils/functions";
import Xfactor from "../Players/Xfactor";
import { useNavigate } from "react-router-dom";
import LibraryAddRoundedIcon from "@mui/icons-material/LibraryAddRounded";
import CloseIcon from "@mui/icons-material/Close";
import "../../assets/compare.css";
import Modal from "@mui/material/Modal";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import { demoCompare } from "../Utils/demoCompare";

const CompareCard = ({ similarPlayers, onRemovePlayer }) => {
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(null);
  const [selectedSeasons, setSelectedSeasons] = useState({});
  const [seasonCards, setSeasonCards] = useState({});

  const handlePlayerClick = (playerId) => {
    navigate(`/player/${playerId}`);
  };

  const handleOpenModal = (player, event) => {
    event.stopPropagation();
    setOpenModal(player);
    // Initialize selected seasons for this player if not already done
    if (!selectedSeasons[player.player_id]) {
      setSelectedSeasons({
        ...selectedSeasons,
        [player.player_id]: [],
      });
    }
  };

  const handleCloseModal = () => {
    setOpenModal(null);
  };

  const handleSeasonCheckboxChange = (playerId, season, checked) => {
    const updatedSeasons = { ...selectedSeasons };

    if (checked) {
      // Add season if it's not already selected
      if (!updatedSeasons[playerId]?.includes(season)) {
        updatedSeasons[playerId] = [
          ...(updatedSeasons[playerId] || []),
          season,
        ];
      }
    } else {
      // Remove season if it's selected
      updatedSeasons[playerId] = (updatedSeasons[playerId] || []).filter(
        (s) => s !== season
      );
    }

    setSelectedSeasons(updatedSeasons);
  };

  const handleAddSeasons = (player) => {
    const playerSeasons = selectedSeasons[player.player_id] || [];

    if (playerSeasons.length === 0) {
      handleCloseModal();
      return;
    }

    // Create season cards for the selected seasons
    const newSeasonCards = { ...seasonCards };

    if (!newSeasonCards[player.player_id]) {
      newSeasonCards[player.player_id] = [];
    }

    // Add new season cards
    playerSeasons.forEach((season) => {
      // Check if this season card already exists
      const seasonExists = newSeasonCards[player.player_id].some(
        (card) => card.season === season
      );

      if (!seasonExists) {
        newSeasonCards[player.player_id].push({
          ...player,
          season,
          ...demoCompare, // Add demo data
        });
      }
    });

    setSeasonCards(newSeasonCards);
    handleCloseModal();
  };

  // Function to remove a season card
  const handleRemoveCard = (event, playerId, season) => {
    event.stopPropagation(); // Prevent card click event

    const updatedSeasonCards = { ...seasonCards };

    // Filter out the card to be removed
    updatedSeasonCards[playerId] = updatedSeasonCards[playerId].filter(
      (card) => card.season !== season
    );

    // If no more season cards for this player, remove the player entry
    if (updatedSeasonCards[playerId].length === 0) {
      delete updatedSeasonCards[playerId];
    }

    setSeasonCards(updatedSeasonCards);
  };

  // Function to remove an original player card and all its associated season cards
  const handleRemoveOriginalCard = (event, playerId) => {
    event.stopPropagation(); // Prevent card click event

    // Remove all season cards for this player
    const updatedSeasonCards = { ...seasonCards };
    if (updatedSeasonCards[playerId]) {
      delete updatedSeasonCards[playerId];
      setSeasonCards(updatedSeasonCards);
    }

    // Find the index of the player in similarPlayers
    const playerIndex = similarPlayers.findIndex(
      (p) => p.player_id === playerId
    );

    // If the player exists in similarPlayers, call the parent component's onRemovePlayer function
    if (playerIndex !== -1 && onRemovePlayer) {
      onRemovePlayer(playerId);
    }
  };

  // Combine original players with their season cards for rendering
  const getAllCards = () => {
    const allCards = [...similarPlayers];

    // Add season cards
    Object.keys(seasonCards).forEach((playerId) => {
      seasonCards[playerId].forEach((seasonCard) => {
        allCards.push(seasonCard);
      });
    });

    return allCards;
  };

  // Safe wrapper for analysisDescription to handle demo data
  const safeAnalysisDescription = (value) => {
    // If value is not a valid number (like "TBD" or "?"), return a default object
    if (value === "TBD" || value === "?" || isNaN(Number(value))) {
      return { level: value, color: "#888" };
    }
    // Otherwise use the original function
    return analysisDescription(value) || { level: value, color: "#888" };
  };

  return (
    <div className="sim-player-comp-grid">
      {getAllCards().map((player, index) => (
        <div
          className="sim-player-comp-card"
          key={`${player.player_id}-${player.season || "career"}-${index}`}
          onClick={() => handlePlayerClick(player.player_id)}
          style={{ cursor: "pointer", position: "relative" }}
        >
          {/* Close button - show for all cards */}
          <button
            className="sim-player-comp-card-close"
            onClick={(e) =>
              player.season
                ? handleRemoveCard(e, player.player_id, player.season)
                : handleRemoveOriginalCard(e, player.player_id)
            }
            aria-label="Close card"
          >
            <CloseIcon fontSize="small" />
          </button>

          <div className="sim-player-comp-inner">
            <div className="sim-player-comp-front">
              <div className="sim-player-comp-image-wrapper">
                <img src={player.image_url} alt={player.player_name} />
                <Xfactor
                  score={
                    player.xfactor === "?"
                      ? "?"
                      : parseInt(player.xfactor, 10).toString()
                  }
                  placement={"gallery"}
                />
              </div>
              <div className="sim-player-comp-info">
                <h4>{player.player_name}</h4>
                <p className="sim-player-comp-position">{player.position}</p>
                <p className="sim-player-comp-school">
                  {player.season || "Career"}
                </p>

                {/* Season Compare Modal - Only show for original player cards, not season cards */}
                {!player.season && (
                  <Modal
                    open={openModal?.player_id === player.player_id}
                    onClose={handleCloseModal}
                    className="sim-player-comp-modal"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      className="sim-player-comp-modal-content"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="sim-player-comp-modal-close"
                        onClick={handleCloseModal}
                      >
                        ×
                      </button>
                      <div className="sim-player-comp-modal-header">
                        <img
                          src={player.image_url}
                          alt={player.player_name}
                          className="sim-player-comp-modal-img"
                        />
                        <h3>{player.player_name}</h3>
                      </div>
                      <div className="sim-player-comp-modal-seasons">
                        {["2020-21", "2021-22", "2022-23", "2023-24"].map(
                          (season) => (
                            <label
                              key={season}
                              className="sim-player-comp-season-item"
                            >
                              <Checkbox
                                checked={
                                  selectedSeasons[player.player_id]?.includes(
                                    season
                                  ) || false
                                }
                                onChange={(e) =>
                                  handleSeasonCheckboxChange(
                                    player.player_id,
                                    season,
                                    e.target.checked
                                  )
                                }
                              />
                              <span>{season}</span>
                            </label>
                          )
                        )}
                      </div>
                      <button
                        className="sim-player-comp-modal-compare-btn"
                        onClick={() => handleAddSeasons(player)}
                      >
                        Add
                      </button>
                    </Box>
                  </Modal>
                )}

                <div className="sim-player-comp-stats">
                  <div className="sim-player-comp-stat-item">
                    <span className="sim-player-comp-stat-label">
                      Ball Handling:
                    </span>
                    <span
                      className="sim-player-comp-stat-value"
                      style={{
                        color: safeAnalysisDescription(player.ball_handling)
                          .color,
                      }}
                    >
                      {safeAnalysisDescription(player.ball_handling).level}
                    </span>
                  </div>
                  <div className="sim-player-comp-stat-item">
                    <span className="sim-player-comp-stat-label">Scoring:</span>
                    <span
                      className="sim-player-comp-stat-value"
                      style={{
                        color: safeAnalysisDescription(player.scoring).color,
                      }}
                    >
                      {safeAnalysisDescription(player.scoring).level}
                    </span>
                  </div>
                  <div className="sim-player-comp-stat-item">
                    <span className="sim-player-comp-stat-label">
                      Rebounding:
                    </span>
                    <span
                      className="sim-player-comp-stat-value"
                      style={{
                        color: safeAnalysisDescription(player.rebounding).color,
                      }}
                    >
                      {safeAnalysisDescription(player.rebounding).level}
                    </span>
                  </div>
                  <div className="sim-player-comp-stat-item">
                    <span className="sim-player-comp-stat-label">Defense:</span>
                    <span
                      className="sim-player-comp-stat-value"
                      style={{
                        color: safeAnalysisDescription(player.defense).color,
                      }}
                    >
                      {safeAnalysisDescription(player.defense).level}
                    </span>
                  </div>
                  <div className="sim-player-comp-stat-item">
                    <span className="sim-player-comp-stat-label">
                      Playmaking:
                    </span>
                    <span
                      className="sim-player-comp-stat-value"
                      style={{
                        color: safeAnalysisDescription(player.playmaking).color,
                      }}
                    >
                      {safeAnalysisDescription(player.playmaking).level}
                    </span>
                  </div>
                  {/* Only show Seasons button for original player cards, not season cards */}
                  {!player.season && (
                    <button
                      className="sim-player-comp-seasons-btn"
                      onClick={(e) => handleOpenModal(player, e)}
                    >
                      <LibraryAddRoundedIcon className="sim-player-comp-seasons-icon" />
                      <span>Seasons</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CompareCard;
